import { useEffect, useRef } from "react";
import { useOpsStore } from "../state/opsStore";

/**
 * Phase 18 WU-18.D5 — Ops Console live updates.
 *
 * Opens a dedicated WebSocket connection to `/ws`, authenticates with the
 * session cookie, and subscribes to `ops:<tenantId>` + `queue:<tenantId>`.
 * Routes incoming messages to opsStore upsert methods.
 *
 * Staleness guard: if the socket drops for >30s, flip
 * `reconnecting=true` so the page shows the yellow banner and
 * `useOpsSnapshot` starts 15s polling. When the socket is back,
 * flip it off.
 */

type OpsMsg =
  | { type: "operator_metrics_tick"; operatorId: string; kpm5m: number; currentDocId: string; currentStep: string }
  | { type: "batch_sla_tick"; batchId: string; elapsedMs: number; slaStatus: "ok" | "approaching" | "breached" }
  | { type: "sla_approaching"; batchId: string; elapsedMs: number }
  | { type: "document:slaBreached"; batchId: string; elapsedMs: number }
  | { type: "bottleneck_detected"; step: string; stallCount: number; avgMs: number; historicalAvgMs: number }
  | { type: string; [k: string]: unknown };

const RECONNECT_GRACE_MS = 30_000;

export function useOpsLiveUpdates(tenantId: string | null): void {
  const upsertOperatorTick = useOpsStore((s) => s.upsertOperatorTick);
  const upsertBatchTick = useOpsStore((s) => s.upsertBatchTick);
  const addBottleneck = useOpsStore((s) => s.addBottleneck);
  const setReconnecting = useOpsStore((s) => s.setReconnecting);

  const wsRef = useRef<WebSocket | null>(null);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelayRef = useRef(1000);

  useEffect(() => {
    if (!tenantId) return;

    let closed = false;

    function connect() {
      if (closed) return;
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${protocol}//${window.location.host}/ws`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectDelayRef.current = 1000;
        // Phase 20/B4 — cookie-based auth. The server (B3) resolves the
        // sessionId from the upgrade request cookie. Client sends a minimal
        // { type: "auth" } frame; no sessionId payload required.
        ws.send(JSON.stringify({ type: "auth" }));
      };

      ws.onmessage = (ev) => {
        let msg: OpsMsg;
        try {
          msg = JSON.parse(ev.data as string) as OpsMsg;
        } catch {
          return;
        }
        if (msg.type === "ping") {
          ws.send(JSON.stringify({ type: "pong" }));
          return;
        }
        if (msg.type === "auth_ok") {
          ws.send(JSON.stringify({ type: "subscribe", channel: `ops:${tenantId}` }));
          ws.send(JSON.stringify({ type: "subscribe", channel: `queue:${tenantId}` }));
          // Healthy connection — clear any pending grace timer.
          if (graceTimerRef.current) {
            clearTimeout(graceTimerRef.current);
            graceTimerRef.current = null;
          }
          setReconnecting(false);
          return;
        }
        if (msg.type === "operator_metrics_tick" && typeof msg.operatorId === "string") {
          upsertOperatorTick({
            operatorId: msg.operatorId,
            kpm5m: typeof msg.kpm5m === "number" ? msg.kpm5m : 0,
            currentDocId: typeof msg.currentDocId === "string" ? msg.currentDocId : "",
            currentStep: typeof msg.currentStep === "string" ? msg.currentStep : "",
          });
        } else if (msg.type === "batch_sla_tick" && typeof msg.batchId === "string") {
          const status: "ok" | "approaching" | "breached" =
            msg.slaStatus === "approaching" || msg.slaStatus === "breached"
              ? msg.slaStatus
              : "ok";
          upsertBatchTick({
            batchId: msg.batchId,
            elapsedMs: typeof msg.elapsedMs === "number" ? msg.elapsedMs : 0,
            slaStatus: status,
          });
        } else if (msg.type === "sla_approaching" && typeof msg.batchId === "string") {
          upsertBatchTick({
            batchId: msg.batchId,
            elapsedMs: typeof msg.elapsedMs === "number" ? msg.elapsedMs : 0,
            slaStatus: "approaching",
          });
        } else if (msg.type === "document:slaBreached" && typeof msg.batchId === "string") {
          upsertBatchTick({
            batchId: msg.batchId,
            elapsedMs: typeof msg.elapsedMs === "number" ? msg.elapsedMs : 0,
            slaStatus: "breached",
          });
        } else if (msg.type === "bottleneck_detected" && typeof msg.step === "string") {
          addBottleneck({
            step: msg.step,
            stallCount: typeof msg.stallCount === "number" ? msg.stallCount : 0,
            avgMs: typeof msg.avgMs === "number" ? msg.avgMs : 0,
            historicalAvgMs:
              typeof msg.historicalAvgMs === "number" ? msg.historicalAvgMs : 0,
          });
        }
      };

      ws.onclose = () => {
        if (closed) return;
        // Start the 30s grace. If we're still not reconnected when it
        // fires, flip to polling mode.
        if (!graceTimerRef.current) {
          graceTimerRef.current = setTimeout(() => {
            setReconnecting(true);
          }, RECONNECT_GRACE_MS);
        }
        const delay = reconnectDelayRef.current;
        reconnectDelayRef.current = Math.min(delay * 2, 30_000);
        setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    connect();
    return () => {
      closed = true;
      if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [tenantId, upsertOperatorTick, upsertBatchTick, addBottleneck, setReconnecting]);
}
